import { Alert, Autocomplete, Box, Stack, TextField } from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import { strongAutocompletePaperProps } from "components/common/dropdownPaper";
import {
  IntegrationConfigProfile,
  useGetConfigProfilesQuery,
  useShareIntegrationConfigMutation,
} from "features/integrationsBeta/configProfiles";
import {
  IntegrationDetail,
  useUpdateIntegrationMutation,
} from "features/integrationsBeta/integrationsBeta";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useState } from "react";
import { formatApiError } from "utils/formatApiError";

/**
 * Dialogs for the shared-config flows on the Integration Config tab:
 * linking an existing profile and converting the current config into a new
 * shared profile (atomic backend action).
 */

export function LinkProfileDialog({
  open,
  onClose,
  integration,
  schemaKey,
}: {
  open: boolean;
  onClose: () => void;
  integration: IntegrationDetail;
  schemaKey: string;
}) {
  const dispatch = useAppDispatch();
  const { data: profiles = [], isLoading: loadingProfiles } =
    useGetConfigProfilesQuery({ client: integration.client });
  const [updateIntegration, { isLoading }] = useUpdateIntegrationMutation();
  const [selected, setSelected] = useState<IntegrationConfigProfile | null>(
    null,
  );
  const [apiError, setApiError] = useState<string | null>(null);

  // A blank profile key is a wildcard; otherwise it must match the
  // integration's resolved schema key (the backend re-validates).
  const compatible = profiles.filter(
    (p) => !p.config_schema_key || p.config_schema_key === schemaKey,
  );

  const handleLink = async () => {
    if (!selected) return;
    try {
      await updateIntegration({
        id: integration.id,
        config_profile: selected.id,
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: `Linked shared config '${selected.name}'.`,
        }),
      );
      onClose();
    } catch (err) {
      setApiError(
        formatApiError(
          (err as { data?: unknown })?.data,
          "Could not link the profile.",
        ),
      );
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Link shared config profile"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleLink}
            disabled={isLoading || !selected}
          >
            Link
          </CustomButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Autocomplete
          componentsProps={strongAutocompletePaperProps}
          options={compatible}
          loading={loadingProfiles}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          getOptionLabel={(p) => p.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, p) => (
            <Box component="li" {...props} key={p.id}>
              <Box sx={{ minWidth: 0 }}>
                {p.name}
                <Box
                  component="span"
                  sx={{ ml: 1, color: "text.secondary", fontSize: "0.8em" }}
                >
                  used by {p.used_by} integration(s)
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Config profile"
              placeholder="Search profiles..."
            />
          )}
          noOptionsText="No compatible profiles for this client."
        />
        <Alert severity="warning">
          After linking, runs use the profile's configuration. The integration's
          own config stays stored but inert until you unlink.
        </Alert>
        {apiError && <Alert severity="error">{apiError}</Alert>}
      </Stack>
    </AppDialog>
  );
}

export function ConvertToProfileDialog({
  open,
  onClose,
  integration,
}: {
  open: boolean;
  onClose: () => void;
  integration: IntegrationDetail;
}) {
  const dispatch = useAppDispatch();
  const [shareConfig, { isLoading }] = useShareIntegrationConfigMutation();
  const [name, setName] = useState(`${integration.name} config`);
  const [apiError, setApiError] = useState<string | null>(null);

  const handleConvert = async () => {
    if (!name.trim()) return;
    try {
      const profile = await shareConfig({
        integrationId: integration.id,
        name: name.trim(),
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: `Shared config '${profile.name}' created. Link other integrations to it to share this configuration.`,
        }),
      );
      onClose();
    } catch (err) {
      setApiError(
        formatApiError(
          (err as { data?: unknown })?.data,
          "Could not convert the configuration.",
        ),
      );
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Convert to shared config"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleConvert}
            disabled={isLoading || !name.trim()}
          >
            Convert
          </CustomButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          autoFocus
          size="small"
          label="Profile name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="Unique per client, e.g. 'Premium Loaves employee sync'."
        />
        <Alert severity="info">
          Creates a shared profile from the last saved configuration and links
          this integration to it. Other integrations of the same client can then
          link the profile to share one config.
        </Alert>
        {apiError && <Alert severity="error">{apiError}</Alert>}
      </Stack>
    </AppDialog>
  );
}
